/**
 * Content scoping (W3-03 UX-029): which columns a document change can reach at all, and the beat window of the
 * note pixels it can move. `TimelineHost` asks every column view that opts in (`DocScopeView.contentAffected`)
 * with the ChangeSet's dirty scopes, its ops and the padded beat range the column's content layer is drawn for,
 * so a commit redraws only the columns whose pixels can change: a note edit no longer repaints every motion
 * column of every line, a key edit skips the note lanes, and an off-screen edit redraws nothing on screen.
 */

import type { NoteId } from '../../core/model/types';
import type { DirtyScopes, Op } from '../../core/ops/types';
import type { Beat } from '../../core/time/types';
import { Beats } from '../../core/time/beat';

/** A closed beat range (f64 beats, the timeline axis' unit). */
export interface BeatWindow {
  readonly lo: number;
  readonly hi: number;
}

/**
 * A column view that can tell whether a document change can reach its content layer. The host asks only about
 * line-scoped changes; global scopes (`isGlobalDirty`) redraw every content layer without asking. `range` is the
 * beat range the content layer is drawn for (the strip's range, padded for glyph overhang); `ops` is the
 * ChangeSet's ops when the caller passed them. Absent = always redrawn; when unsure, answer true.
 */
export interface DocScopeView {
  contentAffected?(dirty: DirtyScopes, ops: readonly Op[] | undefined, range: readonly [number, number]): boolean;
}

/**
 * True when the change is not line-scoped (tempo map, timing, chart metadata, package settings, the line set,
 * effects, ...): every content layer can change and the views are not asked.
 */
export function isGlobalDirty(d: DirtyScopes): boolean {
  return (
    d.tempo ||
    d.timing ||
    d.meta ||
    d.pkg ||
    d.editor ||
    d.rpeRoot ||
    d.lineSet ||
    d.effects ||
    d.markers ||
    d.groups ||
    d.assets ||
    d.generators
  );
}

/** True when two closed ranges intersect (touching ends count). */
export function windowsOverlap(a: BeatWindow, b: BeatWindow): boolean {
  return a.lo <= b.hi && b.lo <= a.hi;
}

/**
 * The beat window of the note pixels a sequence of plain note ops can move: the `t`..`end` of every touched note
 * in both states (a hold's body tapers between its ends, so a one-ended edit still moves pixels across the whole
 * span). `resolve` answers the full extent of a note when the patches leave one of its ends unknown (an x-only
 * move, or a `t`-only move of a hold); a miss there makes the window unknown.
 *
 * Null (unknown, never skips a redraw) when `ops` is absent or any op is not a plain note edit: key and track
 * edits move the curve between their neighbouring keys, so their pixels reach far beyond their own beat, and
 * record kinds that are not beat-local at all are the global scopes' business (`isGlobalDirty`).
 */
export function noteChangeWindow(
  ops: readonly Op[] | undefined,
  resolve?: (id: NoteId) => BeatWindow | null,
): BeatWindow | null {
  if (ops === undefined) return null;
  let lo = Infinity;
  let hi = -Infinity;
  const add = (b: Beat | undefined | null): void => {
    if (b === undefined || b === null) return;
    const x = Beats.toF64(b);
    if (x < lo) lo = x;
    if (x > hi) hi = x;
  };
  const addWindow = (w: BeatWindow): void => {
    if (w.lo < lo) lo = w.lo;
    if (w.hi > hi) hi = w.hi;
  };
  for (const op of ops) {
    if (op.o === 'ins' || op.o === 'del') {
      if (op.tbl !== 'notes') return null;
      add(op.rec.t);
      add(op.rec.end);
    } else if (op.o === 'set') {
      if (op.tbl !== 'notes') return null;
      const haveT = op.prev.t !== undefined || op.next.t !== undefined;
      const haveEnd = op.prev.end !== undefined || op.next.end !== undefined;
      add(op.prev.t);
      add(op.next.t);
      add(op.prev.end);
      add(op.next.end);
      const w = resolve === undefined ? null : resolve(op.id);
      if (w !== null) addWindow(w);
      else if (!haveT || !haveEnd) return null;
    } else {
      return null;
    }
  }
  return lo <= hi ? { lo, hi } : null;
}
